import axios from 'axios';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

const INITIAL_SNAPSHOT = {
  filters: [],
  limit: 5,
};

function normalizeSavedFiltersResponse(data) {
  return {
    filters: Array.isArray(data?.filters) ? data.filters : [],
    limit: Number(data?.limit) || 5,
  };
}

function normalizeTrimmedStringList(value) {
  return Array.isArray(value)
    ? value.flatMap((item) => {
        const trimmed = String(item || '').trim();
        return trimmed ? [trimmed] : [];
      })
    : [];
}

async function fetchSavedSecFilingFilters(apiBaseUrl, getAccessTokenSilently, signal) {
  const token = await getAccessTokenSilently({ detailedResponse: false });
  const endpoint = `${apiBaseUrl}/api/sec-filings/filters`;
  const { data } = await axios.get(endpoint, {
    signal,
    headers: { Authorization: `Bearer ${token}` },
  });
  return normalizeSavedFiltersResponse(data);
}

function createSavedFiltersStore(apiBaseUrl, getAccessTokenSilently, isAuthenticated) {
  let snapshot = INITIAL_SNAPSHOT;
  let controller = null;
  let requestId = 0;
  const listeners = new Set();

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const setSnapshot = (nextSnapshot) => {
    snapshot = nextSnapshot;
    notify();
  };

  const loadSavedFilters = async () => {
    if (!isAuthenticated) {
      setSnapshot(INITIAL_SNAPSHOT);
      return;
    }

    if (controller) controller.abort();
    const activeController = new AbortController();
    controller = activeController;
    const currentRequestId = requestId + 1;
    requestId = currentRequestId;

    try {
      const nextSnapshot = await fetchSavedSecFilingFilters(
        apiBaseUrl,
        getAccessTokenSilently,
        activeController.signal
      );
      if (!activeController.signal.aborted && currentRequestId === requestId) {
        setSnapshot(nextSnapshot);
      }
    } catch (err) {
      const isCanceledRequest = axios.isCancel(err) || err?.name === 'CanceledError';
      if (!isCanceledRequest && currentRequestId === requestId) {
        setSnapshot(INITIAL_SNAPSHOT);
      }
    }
  };

  return {
    getSnapshot: () => snapshot,
    setFilters: (updater) => {
      setSnapshot({
        ...snapshot,
        filters:
          typeof updater === 'function'
            ? updater(snapshot.filters)
            : updater,
      });
    },
    removeFilter: (filterId) => {
      setSnapshot({
        ...snapshot,
        filters: snapshot.filters.filter((filter) => filter.id !== filterId),
      });
    },
    upsertFilter: (filter) => {
      const normalizedFilter = {
        ...filter,
        forms: normalizeTrimmedStringList(filter.forms),
      };
      const without = snapshot.filters.filter((item) => item.id !== normalizedFilter.id);
      setSnapshot({
        ...snapshot,
        filters: [normalizedFilter, ...without].slice(0, snapshot.limit),
      });
    },
    subscribe: (listener) => {
      listeners.add(listener);
      if (listeners.size === 1) loadSavedFilters();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && controller) {
          controller.abort();
          controller = null;
        }
      };
    },
  };
}

export function useSavedSecFilingFiltersData(
  apiBaseUrl,
  getAccessTokenSilently,
  isAuthenticated
) {
  const store = useMemo(
    () => createSavedFiltersStore(apiBaseUrl, getAccessTokenSilently, isAuthenticated),
    [apiBaseUrl, getAccessTokenSilently, isAuthenticated]
  );
  const { filters, limit } = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot
  );

  const removeSavedCustomFilter = useCallback((filterId) => {
    store.removeFilter(filterId);
  }, [store]);

  const upsertSavedCustomFilter = useCallback((filter) => {
    store.upsertFilter(filter);
  }, [store]);

  return {
    removeSavedCustomFilter,
    savedCustomFilterLimit: limit,
    savedCustomFilters: filters,
    upsertSavedCustomFilter,
  };
}
